import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/axios';

const STORAGE_URL = (import.meta as any).env?.VITE_STORAGE_URL || 'https://cartz.cc/storage';

export default function PaymentLinkPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGateway, setSelectedGateway] = useState('');
  const [paying, setPaying] = useState(false);
  const [paymentRedirect, setPaymentRedirect] = useState<any>(null);

  useEffect(() => {
    if (token) loadOrder();
  }, [token]);

  const loadOrder = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payment-link/${token}`);
      setData(res.data);
      if (res.data.payment_gateways?.length > 0) {
        setSelectedGateway(res.data.payment_gateways[0].gateway);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Payment link not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const handlePay = async () => {
    if (!data || !selectedGateway) return;
    setPaying(true);
    try {
      const res = await api.post(`/storefront/${data.store.slug}/orders/${encodeURIComponent(data.order.order_number)}/pay`, {
        gateway: selectedGateway,
      });

      if (res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
      } else if (res.data.form_data) {
        setPaymentRedirect(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Payment failed');
      setPaying(false);
    }
  };

  // Auto-submit KNET form
  useEffect(() => {
    if (paymentRedirect?.form_data) {
      const timer = setTimeout(() => {
        document.getElementById('knet-auto-form')?.dispatchEvent(new Event('submit'));
        const form = document.getElementById('knet-auto-form') as HTMLFormElement;
        if (form) form.submit();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [paymentRedirect]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}><p style={{ textAlign: 'center', color: '#6b7280' }}>Loading...</p></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center', color: '#ef4444', fontSize: 16 }}>{error || 'Not found'}</p>
        </div>
      </div>
    );
  }

  const { order, store, payment_gateways } = data;
  const isPaid = order.payment_status === 'paid';

  // KNET auto-submit form
  if (paymentRedirect?.form_data) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <p style={{ textAlign: 'center' }}>Redirecting to payment gateway...</p>
          <form id="knet-auto-form" method="POST" action={paymentRedirect.form_data.action}>
            {Object.entries(paymentRedirect.form_data.fields || {}).map(([k, v]) => (
              <input key={k} type="hidden" name={k} value={v as string} />
            ))}
          </form>
        </div>
      </div>
    );
  }

  const gatewayLabels: Record<string, string> = {
    tahseeel_knet: 'KNET',
    tahseeel_cc: 'Visa / MasterCard',
    cbk_knet: 'KNET',
    paypal: 'PayPal',
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Store Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {store.logo && (
            <img src={`${STORAGE_URL}/${store.logo}`} alt={store.name} style={{ maxWidth: 80, maxHeight: 80, objectFit: 'contain', marginBottom: 8 }} />
          )}
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{store.name}</h2>
        </div>

        {isPaid ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>&#10003;</div>
            <h3 style={{ color: '#16a34a' }}>Payment Completed</h3>
            <p style={{ color: '#6b7280' }}>Order {order.order_number} has been paid.</p>
          </div>
        ) : (
          <>
            {/* Order Info */}
            <div style={{ background: '#f9fafb', borderRadius: 10, padding: 16, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#6b7280', fontSize: 13 }}>Order</span>
                <strong>{order.order_number}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#6b7280', fontSize: 13 }}>Customer</span>
                <span>{order.customer_name}</span>
              </div>
            </div>

            {/* Items */}
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Items</h3>
              {order.items?.map((item: any) => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div>
                    <span>{item.product_name}</span>
                    <span style={{ color: '#6b7280', fontSize: 13, marginInlineStart: 8 }}>x{item.quantity}</span>
                  </div>
                  <strong>{store.currency_symbol} {Number(item.total).toFixed(3)}</strong>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0', fontWeight: 700, fontSize: 18 }}>
                <span>Total</span>
                <span>{store.currency_symbol} {Number(order.total).toFixed(3)}</span>
              </div>
            </div>

            {/* Payment Method Selection */}
            {payment_gateways.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10 }}>Payment Method</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {payment_gateways.map((gw: any) => (
                    <label
                      key={gw.gateway}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
                        border: `2px solid ${selectedGateway === gw.gateway ? '#4f46e5' : '#e5e7eb'}`,
                        borderRadius: 10, cursor: 'pointer', transition: 'border-color 0.2s',
                      }}
                    >
                      <input
                        type="radio"
                        name="gateway"
                        checked={selectedGateway === gw.gateway}
                        onChange={() => setSelectedGateway(gw.gateway)}
                      />
                      <span style={{ fontWeight: 500 }}>{gatewayLabels[gw.gateway] || gw.gateway}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handlePay}
              disabled={paying || !selectedGateway}
              style={{
                width: '100%', padding: '14px', background: '#4f46e5', color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 600,
                cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.7 : 1,
              }}
            >
              {paying ? '...' : `Pay ${store.currency_symbol} ${Number(order.total).toFixed(3)}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f3f4f6',
    padding: 20,
  },
  card: {
    maxWidth: 480,
    width: '100%',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
    padding: 32,
  },
};
